'use client';

import { useSearchParams } from 'next/navigation';
import { TypedLocale, Where } from 'payload';
import { stringify } from 'qs-esm';
import { useEffect, useState } from 'react';

import { EventsList } from '@/components/Events/EventsList';
import { LoadAllButton } from '@/components/LoadAllButton';
import { SearchField } from '@/components/SearchField';
import { TrackSearch } from '@/components/utilities/Analytics/TrackSearch';
import { Event } from '@/payload-types';
import { cn } from '@/utils/cn';
import { getDictionary } from '@/utils/getDictionary';

import styles from './EventsListTemplate.module.scss';

type Props = {
  cards: Event[];
  hasNextPage: boolean;
  totalDocs: number;
  locale: TypedLocale;
};

export default function EventsListServer({
  cards,
  hasNextPage,
  locale,
  totalDocs,
}: Props) {
  const t = getDictionary(locale);
  const searchParams = useSearchParams();
  const [filteredEventsData, setFilteredEventsData] = useState({
    docs: cards as Event[],
    hasNextPage,
    totalDocs,
  });
  const [initial, setInitial] = useState(true);

  const limit = Number(searchParams.get('limit') || 4);
  const query = searchParams.get('query')?.trim() || '';

  const andConditions: Where[] = [];

  // Add query-based filter if present
  if (query) {
    andConditions.push({
      or: [{ title: { exists: true, like: query } }],
    });
  } else {
    // If no query, just check title exists
    andConditions.push({ title: { exists: true } });
  }

  // Always exclude if 'onDemand' is true
  andConditions.push({ onDemand: { not_equals: true } });

  // Always require published status
  andConditions.push({ _status: { equals: 'published' } });

  const where: Where = { and: andConditions };

  const updateData = async () => {
    const params = {
      locale,
      fallbackLocale: false,
      limit,
      page: 1,
      where,
      // TODO: Select
      sort: '-publishedAt',
    };

    const stringifiedQuery = stringify(params, { addQueryPrefix: true });

    const res = await fetch(`/api/events/?${stringifiedQuery}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Accept-Language': locale,
        'Content-Type': 'application/json',
      },
    });

    const data = await res.json();

    setFilteredEventsData({
      docs: data.docs as Event[],
      hasNextPage: data.hasNextPage,
      totalDocs: data.totalDocs,
    });
  };

  useEffect(() => {
    if (andConditions.length > 0 || !initial) {
      updateData();
    }
    if (initial) {
      setInitial(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return (
    <>
      {query && (
        <TrackSearch query={query} pageType='events' resultCount={totalDocs} />
      )}
      <div className={cn('container', styles.searchAndFilters)}>
        <SearchField wide label={t('events.searchField')} />
      </div>
      <EventsList cards={filteredEventsData.docs} />
      {filteredEventsData.hasNextPage && (
        <LoadAllButton
          currentCount={filteredEventsData.docs.length}
          totalCount={filteredEventsData.totalDocs}
        />
      )}
    </>
  );
}
